
import { useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { Product } from '@/components/product/ProductCard';

export function useProductSelection() {
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  
  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    if (isSelectionMode) {
      setSelectedProducts([]);
    }
  };

  const clearSelection = () => {
    setSelectedProducts([]);
    setIsSelectionMode(false);
  };

  const selectProduct = (product: Product, isSelected: boolean) => {
    if (isSelected) {
      setSelectedProducts(prev => [...prev, product]);
    } else {
      setSelectedProducts(prev => 
        prev.filter(p => !(p.id === product.id && p.source === product.source))
      );
    }
  };

  const selectAll = (products: Product[]) => {
    // Only add products that aren't already selected
    const newSelectedProducts = [...selectedProducts];
    
    products.forEach(product => {
      const isAlreadySelected = selectedProducts.some(
        p => p.id === product.id && p.source === product.source
      );
      
      if (!isAlreadySelected) {
        newSelectedProducts.push(product);
      }
    });
    
    setSelectedProducts(newSelectedProducts);
    
    // If we're selecting products and not in selection mode, enter selection mode
    if (products.length > 0 && !isSelectionMode) {
      setIsSelectionMode(true);
    }
    
    if (products.length > 0) {
      toast.success(`Selected ${products.length} products`);
    }
  };

  const updateCategoryBulk = async (products: Product[], newCategory: string) => {
    try {
      setIsBulkUpdating(true);
      
      // Group products by source
      const productItems = products.filter(p => p.source === 'products');
      const recommendationItems = products.filter(p => p.source === 'recommendations');
      
      // Update products
      if (productItems.length > 0) {
        const { error: productsError } = await supabase
          .from('products')
          .update({ category: newCategory })
          .in('id', productItems.map(p => p.id));
        
        if (productsError) throw productsError;
      }
      
      // Update recommendations
      if (recommendationItems.length > 0) {
        const { error: recommendationsError } = await supabase
          .from('product_recommendations')
          .update({ category: newCategory })
          .in('id', recommendationItems.map(p => p.id));
        
        if (recommendationsError) throw recommendationsError;
      }
      
      toast.success(`Updated category for ${products.length} product(s)`);
      
      // Clear selection after update
      clearSelection();
      
      return true;
    } catch (error: any) {
      console.error('Error updating product categories:', error);
      toast.error(`Failed to update categories: ${error.message}`);
      return false;
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const tagAllProducts = async (products: Product[], newCategory: string) => {
    try {
      setIsBulkUpdating(true);
      
      // Group products by source
      const productItems = products.filter(p => p.source === 'products');
      const recommendationItems = products.filter(p => p.source === 'recommendations');
      
      // Update products
      if (productItems.length > 0) {
        const { error: productsError } = await supabase
          .from('products')
          .update({ category: newCategory })
          .in('id', productItems.map(p => p.id));
        
        if (productsError) throw productsError;
      }
      
      // Update recommendations
      if (recommendationItems.length > 0) {
        const { error: recommendationsError } = await supabase
          .from('product_recommendations')
          .update({ category: newCategory })
          .in('id', recommendationItems.map(p => p.id));
        
        if (recommendationsError) throw recommendationsError;
      }
      
      toast.success(`Tagged ${products.length} product(s) as "${newCategory}"`);
      return true;
    } catch (error: any) {
      console.error('Error tagging products:', error);
      toast.error(`Failed to tag products: ${error.message}`);
      return false;
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const deleteBulk = async (products: Product[]) => {
    try {
      setIsBulkUpdating(true);
      
      // Group products by source
      const productItems = products.filter(p => p.source === 'products');
      const recommendationItems = products.filter(p => p.source === 'recommendations');
      
      // Delete products
      if (productItems.length > 0) {
        const { error: productsError } = await supabase
          .from('products')
          .delete()
          .in('id', productItems.map(p => p.id));
        
        if (productsError) throw productsError;
      }
      
      // Delete recommendations
      if (recommendationItems.length > 0) {
        const { error: recommendationsError } = await supabase
          .from('product_recommendations')
          .delete()
          .in('id', recommendationItems.map(p => p.id));
        
        if (recommendationsError) throw recommendationsError;
      }
      
      toast.success(`Deleted ${products.length} product(s)`);
      
      // Clear selection after delete
      clearSelection();
      
      return true;
    } catch (error: any) {
      console.error('Error deleting products:', error);
      toast.error(`Failed to delete products: ${error.message}`);
      return false;
    } finally {
      setIsBulkUpdating(false);
    }
  };

  return {
    isSelectionMode,
    selectedProducts,
    isBulkUpdating,
    toggleSelectionMode,
    selectProduct,
    selectAll,
    clearSelection,
    updateCategoryBulk,
    deleteBulk,
    tagAllProducts
  };
}
